import React, { useState } from 'react';
import {
    Paper,
    Stack,
    Typography,
    Box,
    Collapse,
    IconButton,
    Tooltip,
} from '@mui/material';
import { ExpandMore, ExpandLess, ContentCopyOutlined, BugReportOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

/**
 * Sprint C (DA-4) — collapsed "Debug info" panel on Order Detail. Today it
 * only surfaces `order.idempotency_key`, which the storefront sends on
 * place-order so backend can dedupe retries within 5 min. Useful when an
 * admin is investigating "why does this customer have two orders for the
 * same cart" — same key = the deduper short-circuited; different keys =
 * two genuine submits. Hidden entirely when the order has no key set
 * (older orders, or future flows that don't pass one).
 */
const OrderDebugInfo = ({ order }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [expanded, setExpanded] = useState(false);

    const idemKey = order?.idempotency_key;
    if (!idemKey) return null;

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(idemKey).then(
            () => toast.success(isArabic ? 'تم النسخ' : 'Copied'),
            () => toast.error(isArabic ? 'تعذر النسخ' : 'Copy failed')
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: 3,
                border: (t) => `1px dashed ${t.palette.divider}`,
                mb: 2,
                bgcolor: (t) =>
                    t.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.02)'
                        : 'rgba(0,0,0,0.015)',
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={() => setExpanded((v) => !v)}
                sx={{ cursor: 'pointer' }}
            >
                <BugReportOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, color: 'text.secondary' }}>
                    {isArabic ? 'معلومات تشخيصية' : 'Debug info'}
                </Typography>
                <IconButton size="small" sx={{ p: 0 }}>
                    {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
            </Stack>

            <Collapse in={expanded} unmountOnExit>
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {isArabic ? 'مفتاح Idempotency' : 'Idempotency Key'}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                flex: 1,
                            }}
                        >
                            {idemKey}
                        </Typography>
                        <Tooltip title={isArabic ? 'نسخ' : 'Copy'}>
                            <IconButton size="small" onClick={handleCopy}>
                                <ContentCopyOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default OrderDebugInfo;
